import React, { useMemo } from 'react';
import {
  formatLocationScope,
  getBookableStaffForService,
  resolveEffectiveLocationIds,
  staffWorksAtAnyLocation,
} from '../utils/locationAssignmentUtils';
import './ServiceBookabilitySummary.css';

const ServiceBookabilitySummary = ({
  staffOptions = [],
  locationOptions = [],
  employeeIds = [],
  locationIds = [],
}) => {
  const summary = useMemo(() => {
    const service = { employeeIds };
    const effectiveLocationIds = resolveEffectiveLocationIds(locationIds, locationOptions);
    const locationScope = formatLocationScope(locationIds, locationOptions);
    const assignedStaff = staffOptions.filter((member) => employeeIds.includes(member.id));
    const bookableStaff = getBookableStaffForService({
      service,
      staff: staffOptions,
      locationIds,
      locations: locationOptions,
    });
    const assignedButNotAtLocation = assignedStaff.filter(
      (member) => !staffWorksAtAnyLocation(member, locationIds, locationOptions)
    );

    return {
      locationScope,
      effectiveLocationIds,
      assignedStaff,
      bookableStaff,
      assignedButNotAtLocation,
    };
  }, [employeeIds, locationIds, locationOptions, staffOptions]);

  if (locationOptions.length <= 1) {
    return null;
  }

  const locationLabel = summary.locationScope.shortLabel || summary.locationScope.names.join(', ');

  return (
    <div className="service-bookability-summary" aria-live="polite">
      <div className="service-bookability-summary__title">Booking preview</div>
      <p className="service-bookability-summary__intro">
        Clients can book when a service is offered at a location <em>and</em> at least one assigned staff member works there.
      </p>

      <dl className="service-bookability-summary__stats">
        <div>
          <dt>Offered at</dt>
          <dd>{locationLabel}</dd>
        </div>
        <div>
          <dt>Assigned staff</dt>
          <dd>{summary.assignedStaff.length}</dd>
        </div>
        <div>
          <dt>Bookable now</dt>
          <dd>{summary.bookableStaff.length}</dd>
        </div>
      </dl>

      {summary.assignedStaff.length > 0 && summary.bookableStaff.length === 0 ? (
        <div className="service-bookability-summary__warning" role="alert">
          No assigned staff work at the selected location{summary.effectiveLocationIds.length === 1 ? '' : 's'}.
          This service will not show available times until you fix staff or location assignments.
        </div>
      ) : null}

      {summary.assignedButNotAtLocation.length > 0 && summary.bookableStaff.length > 0 ? (
        <div className="service-bookability-summary__note" role="status">
          These assigned staff will not appear at the selected location{summary.effectiveLocationIds.length === 1 ? '' : 's'}:
          {' '}
          {summary.assignedButNotAtLocation.map((member) => member.name).join(', ')}.
        </div>
      ) : null}
    </div>
  );
};

export default ServiceBookabilitySummary;
